import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  Plus,
  Users,
  Eye,
  DollarSign,
  RefreshCw,
  CheckCircle,
  Clock,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-700' }
};

export default function AgentStudents() {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    destinationCountry: '',
    interestedServices: [],
    notes: ''
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await agentAPI.getMyStudents();
      setStudents(response.students || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      // Use mock data for demonstration
      setStudents([
        {
          studentId: '1',
          user: { firstName: 'Alex', lastName: 'Thompson', email: 'alex@example.com' },
          phone: '+1 234 567 8900',
          country: 'USA',
          destinationCountry: 'UK',
          status: 'active',
          serviceRequests: [{ status: 'IN_PROGRESS' }, { status: 'COMPLETED' }],
          totalCommission: 450,
          createdAt: new Date().toISOString()
        },
        {
          studentId: '2',
          user: { firstName: 'Priya', lastName: 'Patel', email: 'priya@example.com' },
          phone: '+91 98765 43210',
          country: 'India',
          destinationCountry: 'Canada',
          status: 'active',
          serviceRequests: [{ status: 'IN_PROGRESS' }],
          totalCommission: 320,
          createdAt: new Date(Date.now() - 86400000 * 5).toISOString()
        },
        {
          studentId: '3',
          user: { firstName: 'David', lastName: 'Kim', email: 'david@example.com' },
          phone: '+82 10 1234 5678',
          country: 'South Korea',
          destinationCountry: 'Australia',
          status: 'completed',
          serviceRequests: [{ status: 'COMPLETED' }, { status: 'COMPLETED' }],
          totalCommission: 550,
          createdAt: new Date(Date.now() - 86400000 * 30).toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    if (!newStudent.firstName || !newStudent.lastName || !newStudent.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await agentAPI.referStudent(newStudent);
      toast.success('Student referred successfully');
      setAddModalOpen(false);
      setNewStudent({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        destinationCountry: '',
        interestedServices: [],
        notes: ''
      });
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add student');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: students.length,
    active: students.filter(s => s.status === 'active').length,
    pending: students.filter(s => s.status === 'pending').length,
    totalCommission: students.reduce((sum, s) => sum + (s.totalCommission || 0), 0)
  };

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-students-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">My Students</h1>
            <p className="text-gray-600">Manage your referred students and track their progress</p>
          </div>
          <Button onClick={() => setAddModalOpen(true)} className="bg-emerald-500 hover:bg-emerald-600">
            <Plus className="w-4 h-4 mr-2" />
            Refer New Student
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-4 bg-white border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  <p className="text-sm text-gray-600">Total Students</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="p-4 bg-white border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                  <p className="text-sm text-gray-600">Active Students</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="p-4 bg-white border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="p-4 bg-white border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">${stats.totalCommission.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Total Commission</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Filters */}
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-students-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchStudents}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Students Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredStudents.length === 0 ? (
          <Card className="p-12 text-center bg-white border-0 shadow-sm">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Students Found</h3>
            <p className="text-gray-500 mb-4">Start referring students to earn commissions</p>
            <Button onClick={() => setAddModalOpen(true)} className="bg-emerald-500 hover:bg-emerald-600">
              <Plus className="w-4 h-4 mr-2" />
              Refer Your First Student
            </Button>
          </Card>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/50">
                    <TableHead className="w-[60px]">Photo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>University</TableHead>
                    <TableHead>Preferred Country</TableHead>
                    <TableHead className="text-center">Services</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.map((student) => {
                    const statusConfig = STATUS_CONFIG[student.status] || STATUS_CONFIG.active;
                    const servicesCount = student.serviceRequests?.length || 0;

                    return (
                      <TableRow
                        key={student.studentId}
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => navigate(`${basePath}/students/${student.studentId}`)}
                        data-testid={`student-row-${student.studentId}`}
                      >
                        <TableCell>
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={student.user?.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-teal-500 text-white font-semibold">
                              {student.user?.firstName?.[0]}{student.user?.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>
                        <TableCell className="font-medium text-gray-900">
                          {student.user?.firstName} {student.user?.lastName}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {student.user?.email}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {student.institution || 'Not specified'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {student.interestedCountries?.[0] || student.destinationCountry || 'Not specified'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-medium">
                            {servicesCount} {servicesCount === 1 ? 'service' : 'services'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`${basePath}/students/${student.studentId}`);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </motion.div>
        )}

        {/* Add Student Modal */}
        <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Refer New Student</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newStudent.firstName}
                    onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newStudent.lastName}
                    onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Phone</label>
                <Input
                  type="tel"
                  value={newStudent.phone}
                  onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Country</label>
                  <Input
                    value={newStudent.country}
                    onChange={(e) => setNewStudent({ ...newStudent, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Destination</label>
                  <Input
                    value={newStudent.destinationCountry}
                    onChange={(e) => setNewStudent({ ...newStudent, destinationCountry: e.target.value })}
                    placeholder="UK"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Notes</label>
                <Textarea
                  value={newStudent.notes}
                  onChange={(e) => setNewStudent({ ...newStudent, notes: e.target.value })}
                  placeholder="Any additional information about the student..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddStudent}
                  disabled={submitting}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Refer Student
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </AgentLayout>
  );
}
